import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import { isFieldDisabled } from '../../utils/lockingUtils';
import { getPriorityCategory, getSelectedHabits, formatHabitsForDropdown, getAllTransformationHabits, getAllPriority1Categories, getFilledHabitText } from '../../utils/habitUtils';
import confetti from 'canvas-confetti';
import './SelfAssessment.css';

const CheckIn02 = () => {
    const navigate = useNavigate();
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        // Page 1: Reflection - The past month has been
        reflectionDate: '',
        pastMonthReflection: '',
        mostImpactfulHabit: '',
        easiestHabit: '',
        hardestHabit: '',

        // Page 2: Decision Tree
        decisionTreeDate: '',
        whatLearnedAboutMyself: '',
        consistent80Percent: '',
        feelingProgress: '',
        continueHabits: [],
        addMoreHabits: '',
        setbacks: '',
        reduceSetbacks: '',

        // Page 3: Month 1 - Symptoms Rating
        month1Weight: '',
        month1WeightUnit: 'kg',
        month1BloodPressure: '',
        month1FastingBloodGlucose: '',
        month1GlucoseUnit: 'mmol/L',
        month1WaistCircumference: '',
        month1WaistUnit: 'cm',
        month1Symptom1: '',
        month1Rating1: '',
        month1Symptom2: '',
        month1Rating2: '',
        month1Symptom3: '',
        month1Rating3: '',
        month1Symptom4: '',
        month1Rating4: '',
        month1Symptom5: '',
        month1Rating5: '',
        month1Symptom6: '',
        month1Rating6: '',
        month1Symptom7: '',
        month1Rating7: '',
        month1Symptom8: '',
        month1Rating8: '',
        month1Symptom9: '',
        month1Rating9: '',
        month1Symptom10: '',
        month1Rating10: '',
        month1AbdominalPainRating: '',
        month1AnxietyRating: '',
        month1AbdominalBloatingRating: '',
        month1DepressionRating: '',
        month1AbdominalGasRating: '',
        month1PeriodPainErectileDysfunctionRating: '',

        // Page 4-7: Nutrition, Sleep, Exercise, Stress habits
        nutritionPriority: '',
        nutritionHabits: [],
        nutritionOther: '',

        sleepPriority: '',
        sleepHabits: [],
        sleepOther: '',

        exercisePriority: '',
        exerciseHabits: [],
        exerciseOther: '',

        stressPriority: '',
        stressHabits: [],
        stressOther: '',

        // Page 8: Commit
        commitDate: '',
        commitHabits: '',

        // Page 9: My Progress - 14 days mood tracking
        day1Mood: '',
        day2Mood: '',
        day3Mood: '',
        day4Mood: '',
        day5Mood: '',
        day6Mood: '',
        day7Mood: '',
        day8Mood: '',
        day9Mood: '',
        day10Mood: '',
        day11Mood: '',
        day12Mood: '',
        day13Mood: '',
        day14Mood: '',
        progressNotes: ''
    });

    const [entryId, setEntryId] = useState(null);
    const [saveTimeout, setSaveTimeout] = useState(null);
    // Habbit dropdown 
    const [previousHabits, setPreviousHabits] = useState([]);
    const [customHabit, setCustomHabit] = useState('');

    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });
    const [emojiPopup, setEmojiPopup] = useState({ show: false, emoji: '', day: null, position: null });


    useEffect(() => {
        loadEntry();
        loadTransformationData();
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); loadTransformationData(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
    }, []);

    useEffect(() => {
        if (saveTimeout) clearTimeout(saveTimeout);
        const timeout = setTimeout(() => {
            if (Object.values(formData).some(val => val)) {
                saveEntry();
            }
        }, 2000);
        setSaveTimeout(timeout);

        return () => clearTimeout(timeout);
    }, [formData]);

    const loadEntry = async () => {
        try {
            const response = await api.get('/journal', {
                params: { section: 'checkin-2' }
            });
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];
                setEntryId(entry._id);
                setFormData(entry.data);
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }
        } catch (error) {
            console.error('Error loading check-in #02 entry:', error);
        }
    };


    const loadTransformationData = async () => {
        try {
            const response = await api.get('/journal', {
                params: { section: 'transformation' }
            });
            if (response.data.entries && response.data.entries.length > 0) {
                const transformationData = response.data.entries[0].data;

                // Get all priority #1 categories and their habits
                const priority1Categories = getAllPriority1Categories(transformationData);
                const allPriority1Habits = [];

                priority1Categories.forEach(category => {
                    const habits = getSelectedHabits(transformationData, category.key);
                    habits.forEach((habitObj) => {
                        // Get the filled habit text using the habit label and category
                        const filledText = getFilledHabitText(habitObj.label, category.category, transformationData);

                        allPriority1Habits.push({
                            key: habitObj.key,
                            label: filledText,
                            category: category.name
                        });
                    });
                });

                setPreviousHabits(formatHabitsForDropdown(allPriority1Habits));
            }
        } catch (error) {
            console.error('Error loading transformation data:', error);
        }
    };

    const saveEntry = async (includeStartTime = false) => {
        try {
            const payload = {
                section: 'checkin-2',
                data: formData
            };

            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (entryId) {
                const response = await api.patch(`/journal/${entryId}`, payload);
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            } else {
                const response = await api.post('/journal', payload);
                setEntryId(response.data.entry._id);
                setEntryMetadata({
                    lockedAt: response.data.entry.lockedAt,
                    startedAt: response.data.entry.startedAt,
                    createdAt: response.data.entry.createdAt
                });
            }
        } catch (error) {
            console.error('Error saving check-in #02 entry:', error);
        }
    };

    const handleChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    // const handleCheckboxChange = (field, value, checked) => {
    //     setFormData(prev => {
    //         const currentArray = prev[field] || [];
    //         if (checked) {
    //             return { ...prev, [field]: [...currentArray, value] };
    //         } else {
    //             return { ...prev, [field]: currentArray.filter(item => item !== value) };
    //         }
    //     });
    // };

    // Health metrics validation functions
    const handleCheckboxChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        const currentValues = formData[field] || [];
        const newValues = currentValues.includes(value)
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value];
        setFormData(prev => ({ ...prev, [field]: newValues }));
    };
    const handleWeightChange = (value) => {
        // Allow numbers with up to 2 decimal places
        const regex = /^\d*\.?\d{0,2}$/;
        if (value === '' || regex.test(value)) {
            handleChange('month1Weight', value);
        }
    };

    const handleBloodPressureChange = (value) => {
        // Allow format: ###/### (systolic/diastolic)
        const regex = /^\d{0,3}(\/\d{0,3})?$/;
        if (value === '' || regex.test(value)) {
            handleChange('month1BloodPressure', value);
        }
    };

    const handleGlucoseChange = (value) => {
        // Allow numbers with up to 2 decimal places
        const regex = /^\d*\.?\d{0,2}$/;
        if (value === '' || regex.test(value)) {
            handleChange('month1FastingBloodGlucose', value);
        }
    };

    const handleWaistChange = (value) => {
        // Allow numbers with up to 2 decimal places
        const regex = /^\d*\.?\d{0,2}$/;
        if (value === '' || regex.test(value)) {
            handleChange('month1WaistCircumference', value);
        }
    };

    // Function to get ordinal suffix for priority ranking
    const getOrdinalSuffix = (num) => {
        const numStr = num.toString();
        if (numStr === '1') return '1st';
        if (numStr === '2') return '2nd';
        if (numStr === '3') return '3rd';
        if (numStr === '4') return '4th';
        return numStr + 'th';
    };

    // Enhanced function to handle priority number inputs
    const handlePriorityChange = (field, value) => {
        // Only allow positive numbers
        if (value === '' || (parseInt(value) >= 1 && !isNaN(parseInt(value)))) {
            handleChange(field, value);
        }
    };

    const handleMoodClick = (day, mood, event) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        handleChange(`day${day}Mood`, mood);

        // Get the position of the clicked element for popup positioning
        const rect = event.target.getBoundingClientRect();
        const position = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
        };

        // Show popup
        setEmojiPopup({
            show: true,
            emoji: mood,
            day: day,
            position: position
        });

        // Trigger confetti
        confetti({
            particleCount: 100,
            spread: 70,
            colors: ['#8B4513', '#FFD700', '#D2B48C', '#FFFFFF'], // brown, gold, tan, white
            origin: {
                x: (rect.left + rect.width / 2) / window.innerWidth,
                y: (rect.top + rect.height / 2) / window.innerHeight
            }
        });

        // Hide popup after 1.5 seconds
        setTimeout(() => {
            setEmojiPopup({ show: false, emoji: '', day: null, position: null });
        }, 1500);
    };


    // Habbit dropdown related handlers
    const handleAddHabit = (habitLabel) => {
        if (habitLabel && !formData.continueHabits.includes(habitLabel)) {
            handleChange('continueHabits', [...formData.continueHabits, habitLabel]);
        }
    };

    // Habbit dropdown related handlers
    const handleRemoveHabit = (habitLabel) => {
        handleChange('continueHabits', formData.continueHabits.filter(h => h !== habitLabel));
    };

    // Habbit dropdown related handlers
    const handleAddCustomHabit = () => {
        if (customHabit.trim() && !formData.continueHabits.includes(customHabit.trim())) {
            handleChange('continueHabits', [...formData.continueHabits, customHabit.trim()]);
            setCustomHabit('');
        }
    };

    const moodEmojis = ['😊', '😐', '☹️'];
    // Helper function to render habit text with inline inputs and dropdowns
    const renderHabitWithInputs = (habitText, habitKey, isChecked = false) => {
        // Handle special cases with dropdowns
        if (habitText.includes('Eat (two / three) meals a day')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Eat {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_meals`] || 'two'}
                            onChange={(e) => handleChange(`${habitKey}_meals`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="two">two</option>
                            <option value="three">three</option>
                        </select>
                    ) : '(two / three)'} meals a day
                </span>
            );
        }
        if (habitText.includes('Eat at least (one / two / three) sources of (fibre / vegetable) in each meal')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Eat at least {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_quantity`] || 'one'}
                            onChange={(e) => handleChange(`${habitKey}_quantity`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="one">one</option>
                            <option value="two">two</option>
                            <option value="three">three</option>
                        </select>
                    ) : '(one / two / three)'} sources of (fibre / vegetable) in each meal
                    {/* {isChecked ? (
                            <select
                                value={formData[`${habitKey}_type`] || 'fibre'}
                                onChange={(e) => handleChange(`${habitKey}_type`, e.target.value)}
                                style={{ margin: '0 5px', padding: '2px', fontSize: '13px', borderRadius: '3px' }}
                            >
                                <option value="fibre">fibre</option>
                                <option value="vegetable">vegetable</option>
                            </select>
                        ) : '(fibre / vegetable)'} in each meal */}
                </span>
            );
        }
        if (habitText.includes('Eat a minimum of _____ grams of protein per day')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Eat a minimum of {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_protein`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_protein`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 2px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} grams of protein per day
                </span>
            );
        }
        if (habitText.includes('Have (equal / less) carbs on my plate compared to protein')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Have {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_carbs`] || 'equal'}
                            onChange={(e) => handleChange(`${habitKey}_carbs`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="equal">equal</option>
                            <option value="less">less</option>
                        </select>
                    ) : '(equal / less)'} carbs on my plate compared to protein
                </span>
            );
        }
        if (habitText.includes('Avoid unhealthy (snacks / drinks / snacks and drinks)')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Avoid unhealthy {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_avoid`] || 'snacks'}
                            onChange={(e) => handleChange(`${habitKey}_avoid`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="snacks">snacks</option>
                            <option value="drinks">drinks</option>
                            <option value="snacks and drinks">snacks and drinks</option>
                        </select>
                    ) : '(snacks / drinks / snacks and drinks)'}
                </span>
            );
        }
        if (habitText.includes('Drink _____ litres of water per day')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Drink {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={formData[`${habitKey}_water`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_water`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '13px',
                                margin: '0 2px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                        />
                    ) : '_____'} litres of water per day
                </span>
            );
        }
        if (habitText.includes('Get direct sunlight in the morning (hrs / afternoon)')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Get direct sunlight in the morning and/or afternoon
                </span>
            );
        }
        if (habitText.includes('Consistently go to sleep at _____ and wake up at _____')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Consistently go to sleep at {isChecked ? (
                        <input
                            type="time"
                            value={formData[`${habitKey}_bedtime`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_bedtime`, e.target.value)}
                            style={{
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                        />
                    ) : '_____'} and wake up at {isChecked ? (
                        <input
                            type="time"
                            value={formData[`${habitKey}_waketime`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_waketime`, e.target.value)}
                            style={{
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                        />
                    ) : '_____'}
                </span>
            );
        }
        if (habitText.includes('Wind down and prepare for bed _____ hours before sleeping')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Wind down and prepare for bed {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_winddown`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_winddown`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '_____'} hours before sleeping
                </span>
            );
        }
        if (habitText.includes('Avoid screens _____ hours before sleeping')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Avoid screens {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_screens`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_screens`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '_____'} hours before sleeping
                </span>
            );
        }
        if (habitText.includes('Avoid anything too stimulating or anxiety-inducing _____ hours before bed')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Avoid anything too stimulating or anxiety-inducing {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_stimulating`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_stimulating`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '__.__'} hours before bed (news / intense exercise / scary or thought-provoking media)
                </span>
            );
        }
        if (habitText.includes('Avoid alcohol _____ hours before bed')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Avoid alcohol {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_alcohol`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_alcohol`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '_____'} hours before bed
                </span>
            );
        }
        if (habitText.includes('Have (one / two / three) evening relaxation habits like')) {
            const activities = ['journaling', 'reading', 'meditating', 'praying', 'stretching', 'breathing'];
            const selectedActivities = formData[`${habitKey}_activities`] || [];

            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Have {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_count`] || 'one'}
                            onChange={(e) => handleChange(`${habitKey}_count`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="one">one</option>
                            <option value="two">two</option>
                            <option value="three">three</option>
                        </select>
                    ) : '(one / two / three)'} evening relaxation habits like
                    {isChecked ? (
                        <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', marginLeft: '10px', alignItems: 'center', marginTop: '10px' }}>
                            {activities.map(activity => (
                                <label key={activity} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: selectedActivities.includes(activity) ? '#8b7355' : 'transparent', //#f5f5f5
                                    color: selectedActivities.includes(activity) ? 'white' : '#2c2416',
                                    padding: '6px 8px',
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    border: '1px solid #8b7355',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedActivities.includes(activity)}
                                        onChange={(e) => {
                                            const newSelected = e.target.checked
                                                ? [...selectedActivities, activity]
                                                : selectedActivities.filter(a => a !== activity);
                                            handleChange(`${habitKey}_activities`, newSelected);
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    {activity}
                                </label>
                            ))}
                        </div>
                    ) : ' (journaling / reading / meditating / praying / stretching / breathing)'}
                </span>
            );
        }

        // Exercise specific handlers
        if (habitText.includes('Stretch for _____ minutes _____ times per week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Stretch for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_minutes`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_minutes`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} minutes {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '_____'} times per week
                </span>
            );
        }
        if (habitText.includes('Do any form of physical activity for _____ minutes _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do any form of physical activity for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_minutes`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_minutes`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} minutes {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '_____'} times a week
                </span>
            );
        }
        if (habitText.includes('Reach _____ steps per day')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Reach {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_steps`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_steps`, e.target.value)}
                            style={{
                                width: '80px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0000000"
                        />
                    ) : '_______'} steps per day
                </span>
            );
        }
        if (habitText.includes('Do a (15 / 20 / 30)-minute HIIT work-out')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do a {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_duration`] || '15'}
                            onChange={(e) => handleChange(`${habitKey}_duration`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value="30">30</option>
                        </select>
                    ) : '(15 / 20 / 30)'}-minute HIIT work-out {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Walk for _____ (minutes / km / miles) _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Walk for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_amount`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_amount`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_unit`] || 'minutes'}
                            onChange={(e) => handleChange(`${habitKey}_unit`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="minutes">minutes</option>
                            <option value="km">km</option>
                            <option value="miles">miles</option>
                        </select>
                    ) : '(minutes / km / miles)'} {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Run for _____ (minutes / km / miles) _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Run for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_amount`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_amount`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_unit`] || 'minutes'}
                            onChange={(e) => handleChange(`${habitKey}_unit`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="minutes">minutes</option>
                            <option value="km">km</option>
                            <option value="miles">miles</option>
                        </select>
                    ) : '(minutes / km / miles)'} {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Participate in my sport of choice which is _____ I will do this _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Participate in my sport of choice which is {isChecked ? (
                        <input
                            type="text"
                            value={formData[`${habitKey}_sport`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_sport`, e.target.value)}
                            style={{
                                width: '100px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="sport name"
                        />
                    ) : '_____'} I will do this {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Do an exercise that builds muscles _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do an exercise that builds muscles {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Do some mobility exercises _____ times per week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do some mobility exercises {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times per week
                </span>
            );
        }

        // Stress Management specific handlers
        if (habitText.includes('Spend _____ minutes with myself each morning')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Spend {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_minutes`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_minutes`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} minutes with myself each morning
                </span>
            );
        }
        if (habitText.includes('Maintain a (12 / 13 / 14 / 15 / 16)-hour fast')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Maintain a {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_hours`] || '12'}
                            onChange={(e) => handleChange(`${habitKey}_hours`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="12">12</option>
                            <option value="13">13</option>
                            <option value="14">14</option>
                            <option value="15">15</option>
                            <option value="16">16</option>
                        </select>
                    ) : '(12 / 13 / 14 / 15 / 16)'}-hour fast. These are the hours after dinner where I will not eat or drink until breakfast. This includes all foods and drinks except water
                </span>
            );
        }
        if (habitText.includes('Spend time in nature _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Spend time in nature {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Express my thoughts and feelings to (a trusted friend / health professional / journal / recorder / self)')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Express my thoughts and feelings to {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_method`] || 'a trusted friend'}
                            onChange={(e) => handleChange(`${habitKey}_method`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="a trusted friend">a trusted friend</option>
                            <option value="health professional">health professional</option>
                            <option value="journal">journal</option>
                            <option value="recorder">recorder</option>
                            <option value="self">self</option>
                        </select>
                    ) : '(a trusted friend / health professional / journal / recorder / self)'} {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Do breathwork _____ times (a day / week)')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do breathwork {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_frequency`] || 'a day'}
                            onChange={(e) => handleChange(`${habitKey}_frequency`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="a day">a day</option>
                            <option value="week">week</option>
                        </select>
                    ) : '(a day / week)'}
                </span>
            );
        }
        if (habitText.includes('Engage in my hobbies _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Engage in my hobbies {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Give myself a treat according to my love language _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Give myself a treat according to my love language {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Organise my schedule in advance - e.g (daily / weekly) to-do list')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Organise my schedule in advance - e.g {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_type`] || 'daily'}
                            onChange={(e) => handleChange(`${habitKey}_type`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="daily">daily</option>
                            <option value="weekly">weekly</option>
                        </select>
                    ) : '(daily / weekly)'} to-do list
                </span>
            );
        }
        // Handle other cases with simple underscores or dropdowns
        const parts = habitText.split('_____');
        if (parts.length === 1) {
            return <span style={{ fontSize: '15px', color: '#2c2416' }}>{habitText}</span>;
        }

        return (
            <span style={{ fontSize: '15px', color: '#2c2416' }}>
                {parts.map((part, index) => (
                    <React.Fragment key={index}>
                        {part}
                        {index < parts.length - 1 && (
                            <input
                                type="text"
                                style={{
                                    width: '60px',
                                    padding: '2px 4px',
                                    border: 'none',
                                    borderBottom: '1px solid #2c2416',
                                    background: 'transparent',
                                    fontSize: '15px',
                                    marginLeft: '2px',
                                    marginRight: '2px',
                                    outline: 'none',
                                    color: '#2c2416'
                                }}
                                placeholder=""
                                value={formData[`${habitKey}_${index}`] || ''}
                                onChange={(e) => handleChange(`${habitKey}_${index}`, e.target.value)}
                            />
                        )}
                    </React.Fragment>
                ))}
            </span>
        );
    };

    const isLocked = entryMetadata.lockedAt !== null;
    const modalConfig = getJournalStartConfig('checkin-02');


    return (
        <>
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />
            <div className="self-assessment-container">
                <LockIndicator
                    lockedAt={entryMetadata.lockedAt}
                    startedAt={entryMetadata.startedAt}
                />

                <div className="journal-page-wrapper">
                    <h1 className="journal-title">Evaluation: Check-in #02</h1>

                    {/* Page 10: Reflection - The past month has been */}
                    <div style={{ marginTop: '60px' }}>
                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.reflectionDate}
                                onChange={(e) => handleChange('reflectionDate', e.target.value)}
                                disabled={isLocked}
                            />
                        </div>

                        <div style={{ marginBottom: '60px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px', 
                                color: '#2c2416', marginBottom: '15px'
                            }}>
                                The past month has been
                            </p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.pastMonthReflection}
                                    onChange={(e) => handleChange('pastMonthReflection', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '60px' }}>
                            <p className="handwritten-label"
                                style={{
                                    // fontSize: '22px', 
                                    color: '#2c2416', marginBottom: '15px'
                                }}>
                                I've noticed that the most impactful habit was
                            </p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.mostImpactfulHabit}
                                    onChange={(e) => handleChange('mostImpactfulHabit', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '60px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px', 
                                color: '#2c2416', marginBottom: '15px'
                            }}>
                                The easiest habit to keep was
                            </p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.easiestHabit}
                                    onChange={(e) => handleChange('easiestHabit', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '40px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px', 
                                color: '#2c2416', marginBottom: '15px'
                            }}>
                                The hardest habit to keep was
                            </p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.hardestHabit}
                                    onChange={(e) => handleChange('hardestHabit', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Page 9: Decision Tree - What I've learned about myself */}
                    <div style={{ marginTop: '80px' }}>
                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.decisionTreeDate}
                                onChange={(e) => handleChange('decisionTreeDate', e.target.value)}
                                disabled={isLocked}
                            />
                        </div>

                        <div style={{ marginBottom: '60px' }}>
                            <p className="handwritten-label" style={{
                                //  fontSize: '22px', 
                                color: '#2c2416', marginBottom: '15px'
                            }}>
                                What I've learned about myself is
                            </p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.whatLearnedAboutMyself}
                                    onChange={(e) => handleChange('whatLearnedAboutMyself', e.target.value)}
                                    placeholder=""
                                    rows={5}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        {/* Decision Tree */}
                        <div style={{ marginTop: '60px', padding: '40px 20px' }}>
                            {/* Level 1: 80% Consistency Question */}
                            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                <p style={{ fontSize: '14px', marginBottom: '20px', color: '#2c2416' }}>
                                    Have I been at least 80% consistent with my selected health habits?
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '60px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="consistent80Percent"
                                            value="yes"
                                            checked={formData.consistent80Percent === 'yes'}
                                            onChange={(e) => handleChange('consistent80Percent', e.target.value)}
                                            style={{ marginRight: '8px', cursor: 'pointer' }}
                                            disabled={isLocked}
                                        />
                                        <span style={{ fontSize: '15px' }}>yes</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="consistent80Percent"
                                            value="no"
                                            checked={formData.consistent80Percent === 'no'}
                                            onChange={(e) => handleChange('consistent80Percent', e.target.value)}
                                            style={{ marginRight: '8px', cursor: 'pointer' }}
                                            disabled={isLocked}
                                        />
                                        <span style={{ fontSize: '15px' }}>no</span>
                                    </label>
                                </div>
                            </div>

                            {/* Level 2: Branch based on consistency */}
                            {formData.consistent80Percent && (
                                <div style={{ marginTop: '60px' }}>
                                    {/* Left Branch: If YES - Show progress question */}
                                    {formData.consistent80Percent === 'yes' && (
                                        <div style={{ paddingLeft: '40px', maxWidth: '600px', margin: '0 auto' }}>
                                            <p style={{ fontSize: '13px', marginBottom: '20px', color: '#2c2416', textAlign: 'center' }}>
                                                Am I feeling progress in my overall well-being?
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '30px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="feelingProgress"
                                                        value="yes"
                                                        checked={formData.feelingProgress === 'yes'}
                                                        onChange={(e) => handleChange('feelingProgress', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>yes</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="feelingProgress"
                                                        value="no"
                                                        checked={formData.feelingProgress === 'no'}
                                                        onChange={(e) => handleChange('feelingProgress', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>no</span>
                                                </label>
                                            </div>

                                            {/* Sub-branches for feeling progress */}
                                            {formData.feelingProgress && (
                                                <div style={{ fontSize: '12px', color: '#3d3425', lineHeight: '1.6', marginTop: '30px' }}>
                                                    {formData.feelingProgress === 'yes' && (
                                                        <div>
                                                            <p
                                                            //  style={{ fontWeight: '600', marginBottom: '8px' }}
                                                            >I will continue with the same habits or add</p>
                                                            {/* <div className="journal-line multi-line">
                                                                <textarea
                                                                    value={formData.continueHabits}
                                                                    onChange={(e) => handleChange('continueHabits', e.target.value)}
                                                                    placeholder=""
                                                                    rows={3}
                                                                    style={{ fontSize: '12px', lineHeight: '24px', width: '100%' }}
                                                                    disabled={isLocked}
                                                                />
                                                            </div> */}
                                                            {/* Selected Habits List */}
                                                            {formData.continueHabits && formData.continueHabits.length > 0 && (
                                                                <div style={{
                                                                    marginBottom: '15px'
                                                                }}>
                                                                    {/* <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#78716c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Selected Habits
                                                                    </p> */}
                                                                    {formData.continueHabits.map((habit, index) => (
                                                                        <div key={index} style={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'flex-start',
                                                                            padding: '8px 0',
                                                                            marginBottom: '6px',
                                                                            fontSize: '13px',
                                                                            color: '#2c2416',
                                                                            lineHeight: '1.5',
                                                                            borderBottom: '1px solid #e7e5e4'
                                                                        }}>
                                                                            <span style={{ flex: 1, paddingRight: '10px' }}>{habit}</span>
                                                                            <button
                                                                                onClick={() => handleRemoveHabit(habit)}
                                                                                disabled={isLocked}
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    color: '#dc2626',
                                                                                    cursor: isLocked ? 'not-allowed' : 'pointer',
                                                                                    fontSize: '18px',
                                                                                    padding: '0',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    flexShrink: 0
                                                                                }}
                                                                                title="Remove habit"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Dropdown to add more habits - only show if previousHabits exist */}
                                                            {previousHabits.length > 0 && previousHabits.filter(h => !formData.continueHabits.includes(h.label)).length > 0 && (
                                                                <div style={{ marginBottom: '15px' }}>
                                                                    <select
                                                                        onChange={(e) => {
                                                                            if (e.target.value) {
                                                                                handleAddHabit(e.target.value);
                                                                                e.target.value = '';
                                                                            }
                                                                        }}
                                                                        disabled={isLocked}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '10px 12px',
                                                                            fontSize: '13px',
                                                                            color: '#2c2416',
                                                                            cursor: 'pointer',
                                                                            backgroundColor: 'transparent',
                                                                            border: 'none',
                                                                            borderBottom: '1px solid #8b7355',
                                                                            borderRadius: '0',
                                                                            outline: 'none'
                                                                        }}
                                                                    >
                                                                        <option value="">+ Add another habit...</option>
                                                                        {previousHabits
                                                                            .filter(h => !formData.continueHabits.includes(h.label))
                                                                            .map(habit => (
                                                                                <option key={habit.value} value={habit.label}>
                                                                                    {habit.label}
                                                                                </option>
                                                                            ))
                                                                        }
                                                                    </select>
                                                                </div>
                                                            )}

                                                            {/* Other: Custom habit input - always show */}
                                                            <div style={{ marginTop: '15px' }}>
                                                                <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Other:</label>
                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                                                    <div className="journal-line" style={{ flex: 1 }}>
                                                                        <input
                                                                            type="text"
                                                                            value={customHabit}
                                                                            onChange={(e) => setCustomHabit(e.target.value)}
                                                                            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomHabit()}
                                                                            placeholder="Type custom habit..."
                                                                            disabled={isLocked}
                                                                            style={{ fontSize: '13px' }}
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        onClick={handleAddCustomHabit}
                                                                        disabled={isLocked || !customHabit.trim()}
                                                                        style={{
                                                                            padding: '10px',
                                                                            fontSize: '20px',
                                                                            fontWeight: '400',
                                                                            color: customHabit.trim() ? '#2c2416' : '#a8a29e',
                                                                            backgroundColor: 'transparent',
                                                                            border: 'none',
                                                                            borderBottom: '1px solid ' + (customHabit.trim() ? '#8b7355' : '#d6d3d1'),
                                                                            borderRadius: '0',
                                                                            cursor: (isLocked || !customHabit.trim()) ? 'not-allowed' : 'pointer',
                                                                            transition: 'all 0.2s',
                                                                            width: '45px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {formData.feelingProgress === 'no' && (
                                                        <div>
                                                            <p>I will add a couple more habits like...</p>
                                                            <div className="journal-line multi-line">
                                                                <textarea
                                                                    value={formData.addMoreHabits}
                                                                    onChange={(e) => handleChange('addMoreHabits', e.target.value)}
                                                                    placeholder=""
                                                                    rows={3}
                                                                    style={{ fontSize: '12px', lineHeight: '24px', width: '100%' }}
                                                                    disabled={isLocked}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Right Branch: If NO - Show setbacks */}
                                    {formData.consistent80Percent === 'no' && (
                                        <div style={{ paddingRight: '40px', maxWidth: '600px', margin: '0 auto' }}>
                                            <p style={{ fontSize: '13px', marginBottom: '15px', color: '#2c2416', textAlign: 'center' }}>
                                                The following are setbacks that reduced my consistency
                                            </p>
                                            <div className="journal-line multi-line" style={{ marginBottom: '25px' }}>
                                                <textarea
                                                    value={formData.setbacks}
                                                    onChange={(e) => handleChange('setbacks', e.target.value)}
                                                    placeholder=""
                                                    rows={6}
                                                    style={{ fontSize: '13px', lineHeight: '32px' }}
                                                    disabled={isLocked}
                                                />
                                            </div>

                                            <p style={{ fontSize: '13px', marginBottom: '20px', color: '#2c2416', textAlign: 'center' }}>
                                                Are there ways to reduce these setbacks?
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '20px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="reduceSetbacks"
                                                        value="yes"
                                                        checked={formData.reduceSetbacks === 'yes'}
                                                        onChange={(e) => handleChange('reduceSetbacks', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>yes</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="reduceSetbacks"
                                                        value="no"
                                                        checked={formData.reduceSetbacks === 'no'}
                                                        onChange={(e) => handleChange('reduceSetbacks', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>no</span>
                                                </label>
                                            </div>

                                            {formData.reduceSetbacks && (
                                                <div style={{ fontSize: '12px', color: '#3d3425', lineHeight: '1.5', marginTop: '20px' }}>
                                                    {formData.reduceSetbacks === 'yes' && (
                                                        <div>
                                                            <p>Try again with the same habits but reduce the setbacks</p>
                                                        </div>
                                                    )}
                                                    {formData.reduceSetbacks === 'no' && (
                                                        <div>
                                                            <p>Reduce the number of habits or make the same habits easier</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Bottom Message */}
                            <div style={{ marginTop: '50px', textAlign: 'center' }}>
                                <p style={{ fontSize: '13px', lineHeight: '1.7', color: '#2c2416' }}>
                                    Read your <span
                                        style={{ color: '#6366f1', cursor: 'pointer', textDecoration: 'none' }}
                                        onClick={() => navigate('/journal/self-assessment')}
                                    >
                                        motivation letter
                                    </span> again to remember what you're working towards!
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Page 8: Month 1 - Health Metrics */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Month 1</h2>

                        <div className="month1-health-metrics-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '60px' }}>
                            <div className="journal-line" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Weight</label>
                                    <input
                                        type="text"
                                        value={formData.month1Weight}
                                        onChange={(e) => handleWeightChange(e.target.value)}
                                        placeholder="0.0"
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                </div>
                                <select
                                    value={formData.month1WeightUnit}
                                    onChange={(e) => handleChange('month1WeightUnit', e.target.value)}
                                    className='dropdown-style'
                                    // style={{
                                    //     padding: '8px',
                                    //     border: '1px solid #ccc',
                                    //     borderRadius: '4px',
                                    //     fontSize: '14px',
                                    //     minWidth: '80px',
                                    //     height: '40px'
                                    // }}
                                    disabled={isLocked}
                                >
                                    <option value="kg">kg</option>
                                    <option value="lbs">lbs</option>
                                </select>
                            </div>
                            <div className="journal-line">
                                <label style={{ fontSize: '14px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Blood Pressure</label>
                                <input
                                    type="text"
                                    value={formData.month1BloodPressure}
                                    onChange={(e) => handleBloodPressureChange(e.target.value)}
                                    placeholder="120/80"
                                    disabled={isLocked}
                                    onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                />
                            </div>
                            <div className="journal-line" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Fasting blood glucose</label>
                                    <input
                                        type="text"
                                        value={formData.month1FastingBloodGlucose}
                                        onChange={(e) => handleGlucoseChange(e.target.value)}
                                        placeholder="0.0"
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                </div>
                                <select
                                    value={formData.month1GlucoseUnit}
                                    onChange={(e) => handleChange('month1GlucoseUnit', e.target.value)}
                                    className='dropdown-style'
                                    // style={{
                                    //     padding: '8px',
                                    //     border: '1px solid #ccc',
                                    //     borderRadius: '4px',
                                    //     fontSize: '14px',
                                    //     minWidth: '100px',
                                    //     height: '40px'
                                    // }}
                                    disabled={isLocked}
                                >
                                    <option value="mmol/L">mmol/L</option>
                                    <option value="mg/dL">mg/dL</option>
                                </select>
                            </div>
                            <div className="journal-line" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Waist circumference</label>
                                    <input
                                        type="text"
                                        value={formData.month1WaistCircumference}
                                        onChange={(e) => handleWaistChange(e.target.value)}
                                        placeholder="0.0"
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                </div>
                                <select
                                    value={formData.month1WaistUnit}
                                    onChange={(e) => handleChange('month1WaistUnit', e.target.value)}
                                    className='dropdown-style'
                                    // style={{
                                    //     padding: '8px',
                                    //     border: '1px solid #ccc',
                                    //     borderRadius: '4px',
                                    //     fontSize: '14px',
                                    //     minWidth: '80px',
                                    //     height: '40px'
                                    // }}
                                    disabled={isLocked}
                                >
                                    <option value="cm">cm</option>
                                    <option value="inches">inches</option>
                                </select>
                            </div>
                        </div>

                        <p style={{ fontSize: '13px', marginBottom: '40px', color: '#3d3425', lineHeight: '1.7' }}>
                            Congratulations on prioritising your health for a month! Without reading your first ratings, rate your symptoms out of 10 again - you may compare them afterwards.
                        </p>

                        <p style={{ fontSize: '14px', color: '#3d3425', marginTop: '20px', lineHeight: '1.6' }}>
                            <strong>1</strong> = not severe, intense or frequent (noticeable but very manageable)<br />
                            <strong>10</strong> = very severe, intense or frequent (greatly affects daily life)
                        </p>

                        {/* Symptom Rating Grid */}
                        <div className="symptom-rating-container">
                            {[1, 3, 5, 7, 9].map((num) => (
                                <div key={num} className="symptom-rating-row">
                                    {/* Left Column */}
                                    <div className="symptom-rating-item">
                                        <strong className="symptom-number">{num}.</strong>
                                        <div className="journal-line" style={{ flex: 1, marginBottom: 0 }}>
                                            <input
                                                type="text"
                                                value={formData[`month1Symptom${num}`]}
                                                onChange={(e) => handleChange(`month1Symptom${num}`, e.target.value)}
                                                placeholder=""
                                                disabled={isLocked}
                                            />
                                        </div>
                                        <div className="rating-input-wrapper">
                                            <select
                                                value={formData[`month1Rating${num}`]}
                                                onChange={(e) => handleChange(`month1Rating${num}`, e.target.value)}
                                                className="rating-input"
                                                style={{
                                                    fontFamily: 'Vintage Moon, serif',
                                                    fontSize: '24.5px',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    outline: 'none',
                                                    color: '#2c2416',
                                                    width: '100%'
                                                }}
                                                disabled={isLocked}
                                            >
                                                <option value="">-</option>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                                    <option key={rating} value={rating}>{rating}</option>
                                                ))}
                                            </select>
                                            <span className="rating-divider">/10</span>
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="symptom-rating-item">
                                        <strong className="symptom-number">{num + 1}.</strong>
                                        <div className="journal-line" style={{ flex: 1, marginBottom: 0 }}>
                                            <input
                                                type="text"
                                                value={formData[`month1Symptom${num + 1}`]}
                                                onChange={(e) => handleChange(`month1Symptom${num + 1}`, e.target.value)}
                                                placeholder=""
                                                disabled={isLocked}
                                            />
                                        </div>
                                        <div className="rating-input-wrapper">
                                            <select
                                                value={formData[`month1Rating${num + 1}`]}
                                                onChange={(e) => handleChange(`month1Rating${num + 1}`, e.target.value)}
                                                className="rating-input"
                                                style={{
                                                    fontFamily: 'Vintage Moon, serif',
                                                    fontSize: '24.5px',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    outline: 'none',
                                                    color: '#2c2416',
                                                    width: '100%'
                                                }}
                                                disabled={isLocked}
                                            >
                                                <option value="">-</option>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                                    <option key={rating} value={rating}>{rating}</option>
                                                ))}
                                            </select>
                                            <span className="rating-divider">/10</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Page 7: Month 1 - Symptoms Table */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Month 1</h2>

                        <p style={{ fontSize: '14px', marginBottom: '40px', color: '#2c2416', lineHeight: '1.6' }}>
                            Click any additional symptoms you have been experiencing and give it a rating out of ten.
                        </p>

                        {/* Symptoms Table */}
                        <div className="symptom-table-grid">
                            {/* Header */}
                            <div className="symptom-table-header">
                                <strong>Symptom</strong>
                            </div>
                            <div className="symptom-table-header symptom-table-rating-header">
                                <strong>Rating out of 10</strong>
                            </div>
                            <div className="symptom-table-header symptom-table-header-right">
                                <strong>Symptom</strong>
                            </div>
                            <div className="symptom-table-header symptom-table-rating-header symptom-table-header-last">
                                <strong>Rating out of 10</strong>
                            </div>

                            {/* Row 1 */}
                            <div className="symptom-table-cell symptom-cell-dark">insomnia</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1Rating1}
                                    onChange={(e) => handleChange('month1Rating1', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">heartburn</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1Rating2}
                                    onChange={(e) => handleChange('month1Rating2', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 2 */}
                            <div className="symptom-table-cell symptom-cell-light">joint pain</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1Rating3}
                                    onChange={(e) => handleChange('month1Rating3', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">constipation</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1Rating4}
                                    onChange={(e) => handleChange('month1Rating4', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 3 */}
                            <div className="symptom-table-cell symptom-cell-dark">sinus pressure</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1Rating5}
                                    onChange={(e) => handleChange('month1Rating5', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">diarrhoea</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1Rating6}
                                    onChange={(e) => handleChange('month1Rating6', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 4 */}
                            <div className="symptom-table-cell symptom-cell-light">swelling</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1Rating7}
                                    onChange={(e) => handleChange('month1Rating7', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">excessive thirst</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1Rating8}
                                    onChange={(e) => handleChange('month1Rating8', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 5 */}
                            <div className="symptom-table-cell symptom-cell-dark">palpitations</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1Rating9}
                                    onChange={(e) => handleChange('month1Rating9', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">excessive hunger</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1Rating10}
                                    onChange={(e) => handleChange('month1Rating10', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 6 */}
                            <div className="symptom-table-cell symptom-cell-light">abdominal pain</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1AbdominalPainRating}
                                    onChange={(e) => handleChange('month1AbdominalPainRating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">anxiety</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1AnxietyRating}
                                    onChange={(e) => handleChange('month1AnxietyRating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 7 */}
                            <div className="symptom-table-cell symptom-cell-dark">abdominal bloating</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1AbdominalBloatingRating}
                                    onChange={(e) => handleChange('month1AbdominalBloatingRating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">depression</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.month1DepressionRating}
                                    onChange={(e) => handleChange('month1DepressionRating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 8 */}
                            <div className="symptom-table-cell symptom-cell-light">abdominal gas</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1AbdominalGasRating}
                                    onChange={(e) => handleChange('month1AbdominalGasRating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">period pain/erectile dysfunction</div>
                            <div className="symptom-table-cell rating-cell-lightest">
                                <select
                                    value={formData.month1PeriodPainErectileDysfunctionRating}
                                    onChange={(e) => handleChange('month1PeriodPainErectileDysfunctionRating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon, serif',
                                        fontSize: '24.5px',
                                        border: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: '#2c2416',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                                        <option key={rating} value={rating}>{rating}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <p style={{ fontSize: '13px', marginBottom: '40px', color: '#3d3425', lineHeight: '1.7', textAlign: 'justify' }}>
                            Once you have finished rating your symptoms, you may compare them with your previous self-assessment. Use this as motivation to stay consistent and further challenge yourself - only 2 months left; make it worthwhile!
                        </p>

                        <p style={{ fontSize: '13px', marginBottom: '60px', color: '#3d3425', lineHeight: '1.7', textAlign: 'justify' }}>
                            In the next few pages, you have the opportunity to re-commit to the same health habits that have been helpful. You may also add a few more if you've gotten used to what you've been doing in the previous month. Keep being ever-progressive.
                        </p>
                    </div>

                    {/* Pages 3-6: Nutrition, Sleep, Exercise, Stress */}
                    {/* Page 3: Nutrition */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '30px' }}>Nutrition</h2>

                        <p className="handwritten-label" style={{
                            // fontSize: '22px', 
                            fontStyle: 'italic', marginBottom: '30px'
                        }}>
                            I've ranked this as my

                            <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.nutritionPriority}
                                onChange={(e) => handlePriorityChange('nutritionPriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px',
                                    outline: 'none'
                                }}
                                disabled={isLocked}
                            />
                            <span
                                className='ranking-prefix'
                            // style={{ fontSize: '22px', marginLeft: '5px' }}
                            >
                                {formData.nutritionPriority ? getOrdinalSuffix(formData.nutritionPriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p style={{ fontSize: '13px', marginBottom: '30px', color: '#2c2416' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginBottom: '30px' }}>
                            {[
                                { value: 'meals', label: 'Eat (two / three) meals a day', hasInput: true },
                                { value: 'fasting', label: 'Maintain a (12 / 13 / 14 / 15 / 16)-hour fast. These are the hours after dinner where I will not eat or drink until breakfast. This includes all foods and drinks except water', hasInput: true },
                                { value: 'noSnacking', label: 'Not snack between meals. Any snacks will be eaten directly after meals' },
                                { value: 'avoidUnhealthy', label: 'Avoid unhealthy (snacks / drinks / snacks and drinks)' },
                                { value: 'avoidAlcohol', label: 'Avoid alcohol' },
                                { value: 'fibreVeg', label: 'Eat at least (one / two / three) sources of (fibre / vegetable) in each meal', hasInput: true },
                                { value: 'protein', label: 'Eat at least one source of protein in each meal' },
                                { value: 'proteinGrams', label: 'Eat a minimum of _____ grams of protein per day', hasInput: true },
                                { value: 'healthyFat', label: 'Eat at least one source of healthy fat in each meal' },
                                { value: 'equalCarbs', label: 'Have (equal / less) carbs on my plate compared to protein' },
                                { value: 'avoidProcessed', label: 'Avoid ultra-processed foods' },
                                { value: 'avoidIntolerances', label: 'Avoid my food intolerances' },
                                { value: 'water', label: 'Drink _____ litres of water per day', hasInput: true }
                            ].map((habit, index) => {
                                const isChecked = (formData.nutritionHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('nutritionHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `nutrition_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="journal-line" style={{ marginTop: '30px' }}>
                            <label style={{ fontSize: '13px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Other:</label>
                            <input
                                type="text"
                                value={formData.nutritionOther}
                                onChange={(e) => handleChange('nutritionOther', e.target.value)}
                                placeholder=""
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* Page 4: Sleep */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '20px' }}>Sleep</h2>

                        <p className="handwritten-label" style={{
                            // fontSize: '22px',
                            fontStyle: 'italic', marginBottom: '30px'
                        }}>
                            I've ranked this as my

                            <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.sleepPriority}
                                onChange={(e) => handlePriorityChange('sleepPriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px',
                                    outline: 'none'
                                }}
                                disabled={isLocked}
                            />
                            <span
                                className='ranking-prefix'
                            // style={{ fontSize: '22px', marginLeft: '5px' }}
                            >
                                {formData.sleepPriority ? getOrdinalSuffix(formData.sleepPriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p style={{ fontSize: '13px', marginBottom: '30px', color: '#2c2416' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginBottom: '30px' }}>
                            {[
                                { value: 'sunlight', label: 'Get direct sunlight in the morning (hrs / afternoon)', hasInput: true },
                                { value: 'movement', label: 'Practice my favourite form of movement during the day' },
                                { value: 'noCaffeine', label: 'Have no caffeine within 10 hours before bed' },
                                { value: 'consistent', label: 'Consistently go to sleep at _____ and wake up at _____', hasInput: true },
                                { value: 'windDown', label: 'Wind down and prepare for bed _____ hours before sleeping', hasInput: true },
                                { value: 'homeDim', label: 'Keep my home as dim as possible in the evening and as dark as possible while sleeping' },
                                { value: 'avoidScreens', label: 'Avoid screens _____ hours before sleeping', hasInput: true },
                                { value: 'avoidStimulating', label: 'Avoid anything too stimulating or anxiety-inducing _____ hours before bed (news / info / exercise / scary or thought-provoking media)', hasInput: true },
                                { value: 'lightDinner', label: 'Have an earlier and/or lighter dinner' },
                                { value: 'avoidAlcoholBeforeBed', label: 'Avoid alcohol _____ hours before bed', hasInput: true },
                                { value: 'eveningHabits', label: 'Have (one / two / three) evening relaxation habits like (journaling / reading / meditating / praying / stretching / breathing)', hasInput: true }
                            ].map((habit, index) => {
                                const isChecked = (formData.sleepHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('sleepHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `sleep_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="journal-line" style={{ marginTop: '30px' }}>
                            <label style={{ fontSize: '13px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Other:</label>
                            <input
                                type="text"
                                value={formData.sleepOther}
                                onChange={(e) => handleChange('sleepOther', e.target.value)}
                                placeholder=""
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* Page 5: Exercise */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '20px' }}>Exercise</h2>

                        <p className="handwritten-label" style={{
                            // fontSize: '22px',
                            marginBottom: '30px', color: '#2c2416', lineHeight: '1.6'
                        }}>
                            I've ranked this as my

                            <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.exercisePriority}
                                onChange={(e) => handlePriorityChange('exercisePriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px',
                                    outline: 'none'
                                }}
                                disabled={isLocked}
                            />
                            <span
                                className='ranking-prefix'
                            // style={{ fontSize: '22px', marginLeft: '5px' }}
                            >
                                {formData.exercisePriority ? getOrdinalSuffix(formData.exercisePriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p style={{ fontSize: '13px', marginBottom: '30px', color: '#2c2416' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginBottom: '30px' }}>
                            {[
                                { value: 'stretch', label: 'Stretch for _____ minutes _____ times per week', hasInput: true },
                                { value: 'physical', label: 'Do any form of physical activity for _____ minutes _____ times a week', hasInput: true },
                                { value: 'steps', label: 'Reach _____ steps per day', hasInput: true },
                                { value: 'walk', label: 'Walk for _____ (minutes / km / miles) _____ times a week', hasInput: true },
                                { value: 'run', label: 'Run for _____ (minutes / km / miles) _____ times a week', hasInput: true },
                                { value: 'hiit', label: 'Do a (15 / 20 / 30)-minute HIIT work-out _____ times a week', hasInput: true },
                                { value: 'sport', label: 'Participate in my sport of choice which is _____ I will do this _____ times a week', hasInput: true },
                                { value: 'buildMuscles', label: 'Do an exercise that builds muscles _____ times a week', hasInput: true },
                                { value: 'mobility', label: 'Do some mobility exercises _____ times per week', hasInput: true },
                                { value: 'exerciseRestDay', label: 'Have at least one day of rest and recovery' }
                            ].map((habit, index) => {
                                const isChecked = (formData.exerciseHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('exerciseHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `exercise_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="journal-line" style={{ marginTop: '30px' }}>
                            <label style={{ fontSize: '13px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Other:</label>
                            <input
                                type="text"
                                value={formData.exerciseOther}
                                onChange={(e) => handleChange('exerciseOther', e.target.value)}
                                placeholder=""
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* Page 6: Stress management */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '20px' }}>Stress management</h2>

                        <p className="handwritten-label" style={{
                            //  fontSize: '22px',
                            fontStyle: 'italic', marginBottom: '30px'
                        }}>
                            I've ranked this as my
                            <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.stressPriority}
                                onChange={(e) => handlePriorityChange('stressPriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px',
                                    outline: 'none'
                                }}
                                disabled={isLocked}
                            />
                            <span
                                className='ranking-prefix'
                            // style={{ fontSize: '22px', marginLeft: '5px' }}
                            >
                                {formData.stressPriority ? getOrdinalSuffix(formData.stressPriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p style={{ fontSize: '13px', marginBottom: '30px', color: '#2c2416' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginBottom: '30px' }}>
                            {[
                                { value: 'relax', label: 'Take at least one hour per day to relax' },
                                { value: 'restDay', label: 'Have a full day of rest each week' },
                                { value: 'morningTime', label: 'Spend _____ minutes with myself each morning', hasInput: true },
                                { value: 'schedule', label: 'Organise my schedule in advance - e.g (daily / weekly) to-do list' },
                                { value: 'breathingRoom', label: 'Leave some breathing room in my schedule and avoid overcommitting myself' },
                                { value: 'boundaries', label: 'Enforce boundaries to protect my mental and emotional health' },
                                { value: 'avoidMedia', label: 'Avoid media that is increasing my stress load' },
                                { value: 'nature', label: 'Spend time in nature _____ times a week', hasInput: true },
                                { value: 'express', label: 'Express my thoughts and feelings to (a trusted friend / health professional / journal / recorder / self) _____ times a week', hasInput: true },
                                { value: 'breathwork', label: 'Do breathwork _____ times (a day / week)', hasInput: true },
                                { value: 'grateful', label: 'List 10 things that I am grateful for daily' },
                                { value: 'hobbies', label: 'Engage in my hobbies _____ times a week', hasInput: true },
                                { value: 'loveLanguage', label: 'Give myself a treat according to my love language _____ times a week', hasInput: true }
                            ].map((habit, index) => {
                                const isChecked = (formData.stressHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('stressHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `stress_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="journal-line" style={{ marginTop: '30px' }}>
                            <label style={{ fontSize: '13px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Other:</label>
                            <input
                                type="text"
                                value={formData.stressOther}
                                onChange={(e) => handleChange('stressOther', e.target.value)}
                                placeholder=""
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* Page 2: Commit */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Commit</h2>

                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.commitDate}
                                onChange={(e) => handleChange('commitDate', e.target.value)}
                                disabled={isLocked}
                            />
                        </div>

                        <p style={{ fontSize: '13px', marginBottom: '30px', color: '#2c2416', lineHeight: '1.6' }}>
                            List all the health habits that you selected for a quick reference
                        </p>

                        <p className="handwritten-label" style={{
                            // fontSize: '22px',
                            marginBottom: '30px',
                            color: '#2c2416',
                            lineHeight: '1.6'
                        }}>
                            In the next two weeks, I will...
                        </p>

                        <div className="journal-line multi-line" style={{ marginTop: '30px' }}>
                            <textarea
                                value={formData.commitHabits}
                                onChange={(e) => handleChange('commitHabits', e.target.value)}
                                placeholder=""
                                rows={16}
                                style={{ width: '100%', lineHeight: '32px' }}
                                disabled={isLocked}
                            />
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '13px', lineHeight: '1.6', marginTop: '50px', color: '#3d3425' }}>
                            Aim for 100% consistency but remember that 80% is still effective (and realistic). Make your future self proud - check-in with yourself in two weeks.
                        </p>
                    </div>

                    {/* Page 1: My Progress */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '32px', marginBottom: '20px' }}>My progress</h2>
                        <p className="journal-prompt" style={{ fontSize: '16px', marginBottom: '40px' }}>How are things going?</p>

                        <div style={{ position: 'relative', overflowX: 'auto', margin: '0 -20px', padding: '0 20px' }}>
                            <table className="progress-calendar-table" style={{ minWidth: '320px' }}>
                                <tbody>
                                    {/* Week 1 Header */}
                                    <tr className="progress-calendar-row">
                                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                            <td key={day} className="progress-calendar-header">
                                                Day {day}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 1 Moods */}
                                    <tr className="progress-calendar-row">
                                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                            <td key={day} className="progress-calendar-cell">
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {moodEmojis.map(emoji => (
                                                        <span
                                                            key={emoji}
                                                            onClick={(e) => handleMoodClick(day, emoji, e)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '24px',
                                                                opacity: formData[`day${day}Mood`] === emoji ? 1 : 0.3,
                                                                transition: 'opacity 0.2s',
                                                                userSelect: 'none'
                                                            }}
                                                        >
                                                            {emoji}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 2 Header */}
                                    <tr className="progress-calendar-row">
                                        {[8, 9, 10, 11, 12, 13, 14].map(day => (
                                            <td key={day} className="progress-calendar-header">
                                                Day {day}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 2 Moods */}
                                    <tr className="progress-calendar-row">
                                        {[8, 9, 10, 11, 12, 13, 14].map(day => (
                                            <td key={day} className="progress-calendar-cell">
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {moodEmojis.map(emoji => (
                                                        <span
                                                            key={emoji}
                                                            onClick={(e) => handleMoodClick(day, emoji, e)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '24px',
                                                                opacity: formData[`day${day}Mood`] === emoji ? 1 : 0.3,
                                                                transition: 'opacity 0.2s',
                                                                userSelect: 'none'
                                                            }}
                                                        >
                                                            {emoji}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>

                            {/* Emoji Popup */}
                            {emojiPopup.show && (
                                <div
                                    style={{
                                        position: 'fixed',
                                        top: emojiPopup.position ? emojiPopup.position.y - 60 : '50%',
                                        left: emojiPopup.position ? emojiPopup.position.x - 40 : '50%',
                                        transform: 'translate(-50%, -50%)',
                                        backgroundColor: emojiPopup.emoji === '😊' ? '#FEF3C7' :
                                            emojiPopup.emoji === '😐' ? '#FED7AA' : '#FECACA',
                                        borderRadius: '50%',
                                        width: '80px',
                                        height: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '40px',
                                        zIndex: 1000,
                                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                                        animation: 'popupBounce 0.6s ease-out'
                                    }}
                                >
                                    {emojiPopup.emoji}
                                </div>
                            )}
                        </div>

                        {/* Notes Section */}
                        <div style={{ marginTop: '60px' }}>
                            <h2 className="journal-title" style={{ fontSize: '32px', marginBottom: '30px' }}>Notes</h2>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.progressNotes}
                                    onChange={(e) => handleChange('progressNotes', e.target.value)}
                                    placeholder=""
                                    rows={12}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isFieldDisabled('progressNotes', isLocked)}
                                />
                            </div>
                        </div>
                    </div>

                    <JournalNavigation
                        prevPage="/journal/checkin-01"
                        nextPage="/journal/checkin-03"
                    />
                </div>
            </div>
        </>
    );
};

export default CheckIn02;
